// related to role details - single role operations

import { API_BASE } from '../../utils/constants';
import HttpServiceSingleton from '../HttpService/HttpService';
import { ROLES_PATH } from '../RolesService/constants';

export interface RoleRecommendationsListParams {
  page?: number;
  limit?: number;
  status?: number;
  type?: number;
}

export interface RoleCondition {
  objectName: string;
  operation: string;
  objectValue: string | string[];
}

export interface EntitlementMetaData {
  evKey: number;
  entitlementValueName: string;
  evDisplayName: string;
  evDescription: string | null;
  existingUserCount: number;
  trustScore: number;
  endpoint: {
    endpointKey: number;
    endpointName: string;
    endpointDisplayName: string | null;
    description: string | null;
    logo: string;
  };
  entitlementType: {
    entitlementTypeKey: number;
    entitlementTypeName: string;
    entitlementDisplayName: string;
    description?: string;
  };
}

export interface RoleAction {
  entitlementMetaData: EntitlementMetaData;
}

export interface RoleRecommendation {
  recommendedObjectKey: number;
  name: string;
  userMatchCount: number;
  includedUsers: number;
  impact: string;
  yearlyRequestVolume: number;
  entitlementsCount: number;
  confidence: number;
  conditions: RoleCondition[];
  actions: RoleAction[];
  entitlements?: RoleAction[]; // BE changed actions to entitlements - transformed to 'actions' in service layer
  isNew: boolean;
}

export interface RoleRecommendationDetails {
  success: boolean;
  message: string;
  recommendedObjectKey: number;
  filters: Record<string, unknown>;
  result: {
    data: (RoleRecommendation & {
      description: string;
      comments?: string;
    })[];
    pagination: {
      nextPage: number;
      currentPage: number;
    };
  };
  userAccess: 'edit' | 'view' | 'none';
}

export interface RoleRecommendationsListResponse {
  data: RoleRecommendation[];
  pagination: {
    nextPage: string | null;
    currentPage: number;
  };
  userAccess?: 'edit' | 'view' | 'none';
}

const RoleRecommendationsService = {
  getRoleRecommendations(params: RoleRecommendationsListParams = {}) {
    const queryParams = new URLSearchParams();

    if (params.page) {
      queryParams.append('page', params.page.toString());
    }

    if (params.limit) {
      queryParams.append('limit', params.limit.toString());
    }

    if (params.status) {
      queryParams.append('status', params.status.toString());
    }

    if (params.type) {
      queryParams.append('type', params.type.toString());
    }

    const queryString = queryParams.toString();
    const url = `${API_BASE}${ROLES_PATH}${
      queryString ? `?${queryString}` : ''
    }`;

    return HttpServiceSingleton.get(url);
  },

  getRoleRecommendationDetails(
    recommendedObjectKey: number
  ): Promise<RoleRecommendationDetails> {
    const url = `${API_BASE}${ROLES_PATH}/${recommendedObjectKey}`;

    return HttpServiceSingleton.post(url).then(
      (response: RoleRecommendationDetails) => ({
        ...response,
        result: {
          ...response.result,
          data: response.result.data.map((role) => ({
            ...role,
            // backend sends 'entitlements', map to 'actions' so the app doesn't break
            actions: role.entitlements ?? role.actions,
          })),
        },
      })
    );
  },
};

export default RoleRecommendationsService;
